import { forwardRef, useMemo } from 'react'
import * as THREE from 'three'
import { RoundedBox } from '@react-three/drei'
import { RoundedBoxGeometry } from 'three-stdlib'
import { LINDE_WOORDMERK, LINDE_WOORDMERK_VAK } from './lindeWoordmerk'

/**
 * Gedeelde onderdelen voor de Matic-serie. Alle machines delen dezelfde
 * bouwstenen — vorken met lastrollen, sensorkoppen, markeringslampen — zodat ze
 * als één familie lezen en per model alleen de opbouw verschilt.
 *
 * De maten komen uit opgemeten fabrieksfoto's (zie TMatic.tsx voor de methode):
 * silhouetten uit het alfakanaal, omgerekend naar meters via één harde ankerwaarde
 * — de vorklengte van 1150 mm, want daar moet een europallet op passen.
 */

export interface TruckKleuren {
  rood: string
  zwart: string
}

export const GRIJS = '#6e757e'
export const DONKER = '#141519'
export const ORANJE = '#e8791a'
/** Het antraciet van de Linde-kunststofdelen — gemeten uit de foto's, niet zwart. */
export const ANTRACIET = '#3c3d42'

/* ------------------------------------------------------------------ vorken */

/**
 * Pallettruckvork, opgemeten uit het zijaanzicht.
 *
 * Een vork van een pallettruck is géén balk: hij ligt met de bovenkant op 85 mm
 * (anders past hij niet onder een europallet), wordt naar de punt toe hoger om
 * de tandem-lastrollen te herbergen, en loopt daarna taps toe. Die drie zones
 * zijn wat hem op afstand als vork laat lezen in plaats van als lat.
 */
export function Vork({ x, lengte = 1.15, kleur = '#b81f27' }: {
  x: number
  lengte?: number
  kleur?: string
}) {
  const rolZone = lengte - 0.29
  const punt = lengte - 0.13
  return (
    <group position={[x, 0, 0]}>
      {/* Hiel: hoger, waar de vork in het vorkenframe hangt. 85 mm, niet 104:
          hoger stak hij door de voorste dwarsplank van een pallet op de vorken. */}
      <mesh position={[0, 0.0425, 0.07]} castShadow receiveShadow>
        <boxGeometry args={[0.172, 0.085, 0.14]} />
        <meshStandardMaterial color={kleur} roughness={0.44} metalness={0.4} />
      </mesh>
      {/* Blad */}
      <mesh position={[0, 0.0575, (rolZone + 0.12) / 2]} castShadow receiveShadow>
        <boxGeometry args={[0.17, 0.055, rolZone - 0.12]} />
        <meshStandardMaterial color={kleur} roughness={0.44} metalness={0.4} />
      </mesh>
      {/* Rolzone: het hoogste stuk, met de uitsparing voor de rolarm */}
      <mesh position={[0, 0.0425, (rolZone + punt) / 2]} castShadow receiveShadow>
        <boxGeometry args={[0.17, 0.085, punt - rolZone]} />
        <meshStandardMaterial color={kleur} roughness={0.44} metalness={0.4} />
      </mesh>
      {/* Donkere uitsparing waar de rolarm doorheen steekt */}
      <mesh position={[0, 0.036, rolZone + 0.055]}>
        <boxGeometry args={[0.178, 0.05, 0.075]} />
        <meshStandardMaterial color="#16171a" roughness={0.85} />
      </mesh>
      {/* Tandem-lastrollen: Ø 85 mm op 89 mm hartafstand, allebei van de
          maattekening. Die 89 is geen toeval en ook geen ruimte om in te
          schatten -- met twee wielen van 85 is dat de minimale afstand waarop
          ze elkaar net niet raken. Ze stonden op 80, dus overlappend. */}
      {[rolZone + 0.1, rolZone + 0.189].map((z) => (
        <mesh key={z} position={[0, 0.0425, z]} rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.0425, 0.0425, 0.086, 16]} />
          <meshStandardMaterial color="#8e7a56" roughness={0.72} metalness={0.05} />
        </mesh>
      ))}
      {/* Taps toelopende punt */}
      <mesh position={[0, 0.032, punt + 0.065]} castShadow>
        <boxGeometry args={[0.168, 0.058, 0.13]} />
        <meshStandardMaterial color={kleur} roughness={0.44} metalness={0.4} />
      </mesh>
      <mesh position={[0, 0.019, punt + 0.126]} castShadow>
        <boxGeometry args={[0.162, 0.03, 0.03]} />
        <meshStandardMaterial color={kleur} roughness={0.5} metalness={0.4} />
      </mesh>
      {/* Slijtvlak bovenop: de pallet schuurt daar, dus dat is doffer */}
      <mesh position={[0, 0.0856, lengte * 0.42]}>
        <boxGeometry args={[0.132, 0.004, lengte * 0.62]} />
        <meshStandardMaterial color="#8d1c22" roughness={0.68} metalness={0.45} />
      </mesh>
    </group>
  )
}

/* ------------------------------------------------------------------ sensoren */

/**
 * Navigatiekop bovenop de sensorbalk: een vaste behuizing met een donker
 * venster rondom. Ø 111 mm, opgemeten uit het achteraanzicht (0,139 × de
 * machinebreedte). Het binnenwerk draaide hier eerst zichtbaar rond, met een
 * blauw oplichtend blokje: een echte navigatielaser draait binnen zijn
 * behuizing, en dat zie je niet (21 oktober).
 */
export const Sensorkop = forwardRef<THREE.Group, { schaal?: number }>(
  function Sensorkop({ schaal = 1 }, ref) {
    return (
      <group scale={schaal}>
        <mesh castShadow>
          <cylinderGeometry args={[0.0555, 0.059, 0.022, 20]} />
          <meshStandardMaterial color="#26272b" roughness={0.45} metalness={0.4} />
        </mesh>
        {/* Venster van donker glas rondom; de ref blijft voor wie de kop wil richten. */}
        <group ref={ref} position={[0, 0.026, 0]}>
          <mesh>
            <cylinderGeometry args={[0.05, 0.05, 0.032, 20]} />
            <meshStandardMaterial color="#0d0e11" roughness={0.22} metalness={0.7} />
          </mesh>
          <mesh position={[0, 0, 0.044]}>
            <boxGeometry args={[0.038, 0.022, 0.016]} />
            <meshStandardMaterial color="#0b1016" roughness={0.12} metalness={0.6} />
          </mesh>
        </group>
        <mesh position={[0, 0.048, 0]}>
          <cylinderGeometry args={[0.0555, 0.048, 0.014, 20]} />
          <meshStandardMaterial color="#26272b" roughness={0.5} metalness={0.4} />
        </mesh>
      </group>
    )
  },
)

/**
 * Veiligheidsscanner: de afgeronde blokjes die onder de sensorbalk hangen.
 * Op de foto's zitten er twee schuin naar buiten aan de uiteinden en één recht
 * vooruit in het midden.
 */
export function Scanner({ schaal = 1 }: { schaal?: number }) {
  return (
    <group scale={schaal}>
      <RoundedBox args={[0.086, 0.07, 0.115]} radius={0.022} smoothness={3} castShadow>
        <meshStandardMaterial color="#2a2b2f" roughness={0.44} metalness={0.4} />
      </RoundedBox>
      {/* Lens: donker glas, glimt anders dan de behuizing */}
      <mesh position={[0, -0.002, 0.059]}>
        <boxGeometry args={[0.062, 0.042, 0.004]} />
        <meshStandardMaterial color="#0b1016" roughness={0.12} metalness={0.75} />
      </mesh>
    </group>
  )
}

/** Stereocamera onder het midden van de balk: platter, met een lensraster. */
export function Camera3D() {
  return (
    <group>
      <RoundedBox args={[0.1, 0.062, 0.05]} radius={0.014} smoothness={3} castShadow>
        <meshStandardMaterial color="#2a2b2f" roughness={0.44} metalness={0.4} />
      </RoundedBox>
      {[-0.03, 0.03].map((x) => (
        <mesh key={x} position={[x, 0, 0.027]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.012, 0.012, 0.004, 12]} />
          <meshStandardMaterial color="#0b1016" roughness={0.1} metalness={0.8} />
        </mesh>
      ))}
    </group>
  )
}

/** Oranje markeringslamp: een lensje op een zwarte voet, geen losse bol. */
export function Markeringslamp({ positie, rotatie = [0, 0, 0] }: {
  positie: [number, number, number]
  rotatie?: [number, number, number]
}) {
  return (
    <group position={positie} rotation={rotatie}>
      <mesh>
        <boxGeometry args={[0.028, 0.034, 0.03]} />
        <meshStandardMaterial color="#1c1d20" roughness={0.5} metalness={0.3} />
      </mesh>
      <mesh position={[0.018, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
        <cylinderGeometry args={[0.016, 0.018, 0.012, 12]} />
        <meshStandardMaterial
          color={ORANJE} emissive={ORANJE} emissiveIntensity={0.25} roughness={0.28}
        />
      </mesh>
    </group>
  )
}

/** Klein geel waarschuwingsplaatje op de rode rok, zoals op de foto's. */
export function Waarschuwingsplaat({ positie, rotatie = [0, 0, 0] }: {
  positie: [number, number, number]
  rotatie?: [number, number, number]
}) {
  return (
    <mesh position={positie} rotation={rotatie}>
      <planeGeometry args={[0.07, 0.026]} />
      <meshStandardMaterial color="#d8c23a" roughness={0.6} />
    </mesh>
  )
}

/** Aandrijf- of steunwiel met naaf. */
export function Wiel({
  positie, straal = 0.12, breedte = 0.09,
}: { positie: [number, number, number]; straal?: number; breedte?: number }) {
  return (
    <group position={positie} rotation={[0, 0, Math.PI / 2]}>
      <mesh castShadow>
        <cylinderGeometry args={[straal, straal, breedte, 16]} />
        <meshStandardMaterial color="#17181b" roughness={0.9} />
      </mesh>
      <mesh>
        <cylinderGeometry args={[straal * 0.45, straal * 0.45, breedte + 0.008, 12]} />
        <meshStandardMaterial color="#5a6169" roughness={0.45} metalness={0.6} />
      </mesh>
    </group>
  )
}

/* ------------------------------------------------------------------ merk */

const logoCache = new Map<string, THREE.CanvasTexture>()

/**
 * Het Linde-embleem als canvas-textuur: het witte woordmerk (het echte, uit
 * `lindeWoordmerk.ts`) in een open rode beugel, met het typenummer eronder.
 * Op de foto's is dat het enige wat de zwarte motorkap breekt, dus zonder dat
 * leest die kap als een blok.
 *
 * Eén textuur voor alle machines — hij hangt aan het canvas, niet aan React.
 */
export function useLindeLogo(type: string, beugel = true) {
  return useMemo(() => {
    const sleutel = beugel ? type : `${type}|kaal`
    const eerder = logoCache.get(sleutel)
    if (eerder) return eerder
    const c = document.createElement('canvas')
    c.width = 512
    c.height = 200
    const g = c.getContext('2d')
    if (!g) return null

    // Rode beugel: een afgeronde haak links en rechts, open aan de bovenkant.
    g.strokeStyle = '#d4232e'
    g.lineWidth = 11
    g.lineCap = 'round'
    g.lineJoin = 'round'
    const haak = (spiegel: number) => {
      const x = spiegel > 0 ? 500 : 12
      g.beginPath()
      g.moveTo(x - spiegel * 120, 26)
      g.lineTo(x - spiegel * 26, 26)
      g.quadraticCurveTo(x, 26, x, 62)
      g.lineTo(x, 124)
      g.quadraticCurveTo(x, 160, x - spiegel * 26, 160)
      g.lineTo(x - spiegel * 150, 160)
      g.stroke()
    }
    if (beugel) {
      haak(-1)
      haak(1)
    }

    // Het echte woordmerk, uit het combinatielogo: 320 px breed, gecentreerd,
    // met de bovenkant op 30 px. Daaronder blijft ruimte voor het typenummer.
    const schaal = 320 / LINDE_WOORDMERK_VAK.breedte
    g.save()
    g.translate(256 - (LINDE_WOORDMERK_VAK.x + LINDE_WOORDMERK_VAK.breedte / 2) * schaal, 30 - LINDE_WOORDMERK_VAK.y * schaal)
    g.scale(schaal, schaal)
    g.fillStyle = '#ffffff'
    g.fill(new Path2D(LINDE_WOORDMERK))
    g.restore()

    g.fillStyle = '#d4232e'
    g.font = '700 26px Inter, Arial, sans-serif'
    g.textAlign = 'center'
    g.fillText(type, 262, 148)

    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.anisotropy = 4
    logoCache.set(sleutel, tex)
    return tex
  }, [type, beugel])
}

/** Het embleem als vlak op de carrosserie. */
export function Merkplaat({ positie, rotatie = [0, 0, 0], breedte = 0.34, type = 'T-MATIC', beugel = true }: {
  positie: [number, number, number]
  rotatie?: [number, number, number]
  breedte?: number
  type?: string
  /** De rode beugel eromheen; op de V van de L-MATIC staat alleen het woordmerk. */
  beugel?: boolean
}) {
  const tex = useLindeLogo(type, beugel)
  if (!tex) return null
  return (
    <mesh position={positie} rotation={rotatie}>
      <planeGeometry args={[breedte, breedte * 0.39]} />
      <meshStandardMaterial map={tex} transparent roughness={0.55} metalness={0.1} />
    </mesh>
  )
}

/* ------------------------------------------------------------------ vormen */

/**
 * Afgeronde carrosseriemassa — de basisvorm van elke machine.
 *
 * Met `lak` krijgt hij een blanke lak over de kleur. Dat is het verschil tussen
 * "rood vlak" en "gespoten paneel": het hooglicht van een autolak zit in de
 * lakLAAG en is veel scherper dan het diffuse hooglicht van de kleur eronder.
 * Zonder dat blijft elk paneel plastic, hoe goed de vorm ook klopt.
 */
export function Carrosserie({
  args, positie, rotatie = [0, 0, 0], kleur, straal = 0.035, ruwheid = 0.38, metaal = 0.3,
  lak = false,
}: {
  args: [number, number, number]
  positie: [number, number, number]
  rotatie?: [number, number, number]
  kleur: string
  straal?: number
  ruwheid?: number
  metaal?: number
  lak?: boolean
}) {
  return (
    <RoundedBox
      args={args} position={positie} rotation={rotatie} radius={straal} smoothness={3}
      castShadow receiveShadow
    >
      {lak
        ? (
          <meshPhysicalMaterial
            color={kleur} roughness={ruwheid} metalness={metaal}
            clearcoat={0.5} clearcoatRoughness={0.22}
          />
        )
        : <meshStandardMaterial color={kleur} roughness={ruwheid} metalness={metaal} />}
    </RoundedBox>
  )
}

/**
 * Afgeronde doos die naar boven toe versmalt.
 *
 * Een motorkap die van onder 0,70 m naar boven 0,57 m loopt is met gestapelde
 * dozen niet te maken: je ziet de traptreden. Dit neemt één afgeronde doos en
 * verschuift de punten zijwaarts naar rato van hun hoogte — één vloeiend
 * getrokken vlak, precies wat een spuitgietdeel is.
 */
export function kapGeometrie(
  breedte: number, hoogte: number, diepte: number, straal: number,
  bovenX: number, bovenZ = bovenX, onderX = 1, onderZ = onderX,
) {
  const g = new RoundedBoxGeometry(breedte, hoogte, diepte, 4, straal)
  const pos = g.attributes.position as THREE.BufferAttribute
  for (let i = 0; i < pos.count; i++) {
    const t = (pos.getY(i) + hoogte / 2) / hoogte
    pos.setX(i, pos.getX(i) * (onderX + (bovenX - onderX) * t))
    pos.setZ(i, pos.getZ(i) * (onderZ + (bovenZ - onderZ) * t))
  }
  pos.needsUpdate = true
  g.computeVertexNormals()
  return g
}

/**
 * Gebogen buis langs een pad. Het A-frame van de T-Matic bestaat uit twee
 * gebogen buizen; met rechte balken leest dat direct als bouwpakket.
 */
export function buisGeometrie(
  punten: [number, number, number][], straal: number, segmenten = 28,
) {
  const curve = new THREE.CatmullRomCurve3(punten.map((p) => new THREE.Vector3(...p)))
  return new THREE.TubeGeometry(curve, segmenten, straal, 12, false)
}

/** Boutkoppen als één rijtje — geeft schaal aan een verder leeg paneel. */
export function Bouten({ posities, straal = 0.008, normaal = 'z' }: {
  posities: [number, number, number][]
  straal?: number
  normaal?: 'x' | 'y' | 'z'
}) {
  const rot: [number, number, number] =
    normaal === 'z' ? [Math.PI / 2, 0, 0] : normaal === 'x' ? [0, 0, Math.PI / 2] : [0, 0, 0]
  return (
    <>
      {posities.map((p, i) => (
        <mesh key={i} position={p} rotation={rot}>
          <cylinderGeometry args={[straal, straal, 0.006, 8]} />
          <meshStandardMaterial color="#2c2e33" roughness={0.45} metalness={0.7} />
        </mesh>
      ))}
    </>
  )
}
